/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ContextBase from '@ckeditor/ckeditor5-core/src/context';
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import BalloonEditorBase from '@ckeditor/ckeditor5-editor-balloon/src/ballooneditor';
import InlineEditorBase from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor';
import BaseCommentThreadView from '@ckeditor/ckeditor5-comments/src/comments/ui/view/basecommentthreadview';
import CommentView from '@ckeditor/ckeditor5-comments/src/comments/ui/view/commentview';

import Font from '@ckeditor/ckeditor5-font/src/font';
import FontColorEditing from '@ckeditor/ckeditor5-font/src/fontcolor/fontcolorediting';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import CKFinderUploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageResizeButtons from '@ckeditor/ckeditor5-image/src/imageresize/imageresizebuttons';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import ListProperties from '@ckeditor/ckeditor5-list/src/listproperties';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import Comments from '@ckeditor/ckeditor5-comments/src/comments';
import Mention from '@ckeditor/ckeditor5-mention/src/mention';
import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount';
import CustomMentionView from './views/CustomMentionView';
import RestrictedEditingMode from '@ckeditor/ckeditor5-restricted-editing/src/restrictededitingmode';
import CloudServicesPlugin from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight';

import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import SwitchButtonView from '@ckeditor/ckeditor5-ui/src/button/switchbuttonview';
import BalloonToolbar from '@ckeditor/ckeditor5-ui/src/toolbar/balloon/balloontoolbar';
import IconView from '@ckeditor/ckeditor5-ui/src/icon/iconview';
import Autosave from '@ckeditor/ckeditor5-autosave/src/autosave';
import HtmlEmbed from '@ckeditor/ckeditor5-html-embed/src/htmlembed';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import Notification from '@ckeditor/ckeditor5-ui/src/notification/notification';
import Placeholder from './placeholder/placeholder';
import InsertLibraryItem from './insert-library-item/insert-library-item';
import LibraryItemBox from './insert-library-item/library-item-box/library-item-box';
import { AddMinitableChartPlugin } from './add-minitable-chart/AddMinitableChartPlugin';
import { InlineCommentPlugin } from './inline-comment/InlineCommentPlugin';
import { createPlugin as createPluginGenericWidget } from './plugins/generic-widget';

class Context extends ContextBase {}
class ClassicEditor extends ClassicEditorBase {}
class BalloonEditor extends BalloonEditorBase {}
class InlineEditor extends InlineEditorBase {}
class CommentThreadView extends BaseCommentThreadView {}

const plugins = [
  Font,
  FontColorEditing,
  Essentials,
  CKFinderUploadAdapter,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  BlockQuote,
  CKFinder,
  EasyImage,
  Heading,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResizeButtons,
  Notification,
  Indent,
  InsertLibraryItem,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Placeholder,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TextTransformation,
  Comments,
  Mention,
  WordCount,
  CustomMentionView,
  LibraryItemBox,
  AddMinitableChartPlugin,
  InlineCommentPlugin,
  BalloonToolbar,
  RestrictedEditingMode,
  CloudServicesPlugin,
  ListProperties,
  HtmlEmbed,
  GeneralHtmlSupport,
  SimpleUploadAdapter,
  Subscript,
  Superscript,
  Highlight,
];

const toolbarItems = {
  items: [
    'heading',
    '|',
    'bold',
    'italic',
    'underline',
    'link',
    'bulletedList',
    'numberedList',
    '|',
    'indent',
    'outdent',
    '|',
    'uploadImage',
    'toggleImageCaption',
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    'undo',
    'redo',
    'insertLibraryItem',
    'addMinitableChart',
    'inlineComment',
  ],
};

const inlineToolbarItems = {
  items: [
    'heading',
    '|',
    'bold',
    'italic',
    'underline',
    'link',
    'bulletedList',
    'numberedList',
    '|',
    'indent',
    'outdent',
    '|',
    'uploadImage',
    'toggleImageCaption',
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    'undo',
    'redo',
    'insertLibraryItem',
    'addMinitableChart',
    'inlineComment',
  ],
};

// Plugins to include in the classic editor build.
ClassicEditor.builtinPlugins = plugins;

// Classic editor configuration.
ClassicEditor.defaultConfig = {
  toolbar: toolbarItems,
  image: {
    toolbar: [
      'imageStyle:full',
      'imageStyle:side',
      '|',
      'imageTextAlternative',
    ],
  },
  list: {
    properties: {
      styles: false,
      startIndex: false,
    },
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
  // This value must be kept in sync with the language defined in webpack.config.js.
  language: 'en',
};

ClassicEditor.Context = Context;
ClassicEditor.CommentThreadView = CommentThreadView;
ClassicEditor.ButtonView = ButtonView;
ClassicEditor.CommentView = CommentView;
ClassicEditor.SwitchButtonView = SwitchButtonView;
ClassicEditor.CustomMentionView = CustomMentionView;
ClassicEditor.IconView = IconView;

InlineEditor.builtinPlugins = plugins;
InlineEditor.defaultConfig = {
  toolbar: inlineToolbarItems,
  list: {
    properties: {
      styles: true,
      startIndex: true,
    },
  },
  language: 'en',
};

InlineEditor.Context = Context;
InlineEditor.CommentThreadView = CommentThreadView;
InlineEditor.ButtonView = ButtonView;
InlineEditor.CommentView = CommentView;
InlineEditor.SwitchButtonView = SwitchButtonView;
InlineEditor.CustomMentionView = CustomMentionView;
InlineEditor.IconView = IconView;

// Plugins to include in the ballon editor build.
BalloonEditor.builtinPlugins = plugins;

// Balloon editor configuration.
BalloonEditor.defaultConfig = {
  toolbar: {
    items: ['bold', 'italic', 'link', 'inlineComment'],
  },
  language: 'en',
};

BalloonEditor.Context = Context;
BalloonEditor.ButtonView = ButtonView;

export default {
  ClassicEditor,
  BalloonEditor,
  InlineEditor,
  Placeholder,
  AddMinitableChartPlugin,
  Autosave,
  createPluginGenericWidget,
};
